import { memo, useState, useCallback } from "react"
import "./SettingsDialog.css"

/* ─────────────────────────────────────────────────────────────────────────────
   5 Positions — 4 corners + center only
───────────────────────────────────────────────────────────────────────────── */
const POSITIONS = [
    { key: "top-left", label: "Top Left", gridRow: 1, gridCol: 1 },
    { key: "top-center", label: "Top Center", gridRow: 1, gridCol: 2 },
    { key: "top-right", label: "Top Right", gridRow: 1, gridCol: 3 },
    { key: "", label: "", gridRow: 2, gridCol: 2 },
    { key: "center", label: "Center", gridRow: 2, gridCol: 2 },
    { key: "", label: "", gridRow: 2, gridCol: 2 },
    { key: "bottom-left", label: "Bottom Left", gridRow: 3, gridCol: 1 },
    { key: "bottom-center", label: "Bottom Center", gridRow: 3, gridCol: 2 },
    { key: "bottom-right", label: "Bottom Right", gridRow: 3, gridCol: 3 },
]

/* ─────────────────────────────────────────────────────────────────────────────
   Themes — 4 colours applied to .table-canvas-container via CSS vars
───────────────────────────────────────────────────────────────────────────── */
export const THEMES = [
    {
        key: "crimson",
        label: "Crimson",
        emoji: "🔴",
        desc: "Classic Casino",
        swatch: ["#93352f", "#3f1a19", "#231312"],
        felt: ["#213d7a", "#102048"],
        rim: "#d4a520",
        vars: {
            "--theme-bg": "radial-gradient(ellipse at 50% 50%, #93352f, #833833, #3f1a19, #231312)",
            "--theme-felt": "#102048",
            "--theme-felt-light": "#213d7a",
            "--theme-rim-1": "rgba(240,210,100,0.70)",
            "--theme-rim-2": "rgba(195,155,55,0.90)",
            "--theme-accent": "#f5c842",
        }
    },
    {
        key: "emerald",
        label: "Emerald",
        emoji: "🟢",
        desc: "Vegas Night",
        swatch: ["#1a4a2e", "#0e2d1a", "#040e07"],
        felt: ["#1a4a32", "#0d2b1e"],
        rim: "#7ed957",
        vars: {
            "--theme-bg": "radial-gradient(ellipse at 50% 50%, #1a4a2e, #143d24, #0e2d1a, #040e07)",
            "--theme-felt": "#0d2b1e",
            "--theme-felt-light": "#1a4a32",
            "--theme-rim-1": "rgba(200,240,100,0.60)",
            "--theme-rim-2": "rgba(140,195,55,0.85)",
            "--theme-accent": "#7ed957",
        }
    },
    {
        key: "midnight",
        label: "Midnight",
        emoji: "🔵",
        desc: "Royal Blue",
        swatch: ["#1a2a5e", "#0d1b3e", "#010510"],
        felt: ["#162248", "#0a1428"],
        rim: "#6ea8fe",
        vars: {
            "--theme-bg": "radial-gradient(ellipse at 50% 50%, #1a2a5e, #0d1b3e, #07102a, #010510)",
            "--theme-felt": "#0a1428",
            "--theme-felt-light": "#162248",
            "--theme-rim-1": "rgba(120,160,240,0.65)",
            "--theme-rim-2": "rgba(80,120,200,0.85)",
            "--theme-accent": "#6ea8fe",
        }
    },
    {
        key: "obsidian",
        label: "Obsidian",
        emoji: "⚫",
        desc: "Black Label",
        swatch: ["#2a2a2a", "#111111", "#030303"],
        felt: ["#1e1e1e", "#111111"],
        rim: "#c8b89a",
        vars: {
            "--theme-bg": "radial-gradient(ellipse at 50% 50%, #2a2a2a, #1a1a1a, #111, #030303)",
            "--theme-felt": "#111111",
            "--theme-felt-light": "#1e1e1e",
            "--theme-rim-1": "rgba(200,180,150,0.55)",
            "--theme-rim-2": "rgba(160,140,110,0.80)",
            "--theme-accent": "#c8b89a",
        }
    },
]

/* ─────────────────────────────────────────────────────────────────────────────
   Apply theme CSS vars to .table-canvas-container
───────────────────────────────────────────────────────────────────────────── */
export function applyTheme(themeKey) {
    const theme = THEMES.find(t => t.key === themeKey)
    if (!theme) return

    const container = document.querySelector(".table-canvas-container")
    if (container) {
        Object.entries(theme.vars).forEach(([prop, val]) => {
            container.style.setProperty(prop, val)
        })
        container.dataset.theme = themeKey
    }

    const gameTable = document.querySelector(".truco-game-table")
    if (gameTable) {
        gameTable.style.background = theme.vars["--theme-bg"]
    }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Mini table SVG preview — reacts to theme colours
───────────────────────────────────────────────────────────────────────────── */
function MiniTablePreview({ theme }) {
    const feltLight = theme.felt[0]
    const feltDark = theme.felt[1]
    const rimColor = theme.rim
    const bgFrom = theme.swatch[0]
    const bgTo = theme.swatch[2]

    return (
        <svg viewBox="0 0 120 72" className="sd-mini-table" xmlns="http://www.w3.org/2000/svg">
            {/* Room background */}
            <defs>
                <radialGradient id={`bg-${theme.key}`} cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor={bgFrom} />
                    <stop offset="100%" stopColor={bgTo} />
                </radialGradient>
                <radialGradient id={`felt-${theme.key}`} cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor={feltLight} />
                    <stop offset="100%" stopColor={feltDark} />
                </radialGradient>
            </defs>

            {/* BG */}
            <rect width="120" height="72" fill={`url(#bg-${theme.key})`} />

            {/* Table wood outer oval */}
            <ellipse cx="60" cy="36" rx="52" ry="28" fill="#3c2008" />

            {/* Gold rim glow */}
            <ellipse cx="60" cy="36" rx="50" ry="26"
                fill="none" stroke={rimColor} strokeWidth="3" opacity="0.85" />
            <ellipse cx="60" cy="36" rx="48" ry="24"
                fill="none" stroke={rimColor} strokeWidth="1" opacity="0.45" />

            {/* Felt */}
            <ellipse cx="60" cy="36" rx="46" ry="22" fill={`url(#felt-${theme.key})`} />

            {/* Felt crosshatch pattern */}
            <ellipse cx="60" cy="36" rx="46" ry="22"
                fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0" />

            {/* Inner oval track */}
            <ellipse cx="60" cy="36" rx="40" ry="17"
                fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8" />

            {/* Centre cards (3 mini rectangles) */}
            {[-10, 0, 10].map((x, i) => (
                <rect key={i} x={57 + x} y="29" width="7" height="10"
                    rx="1" fill="#f0e0b8" stroke="rgba(180,148,55,0.5)" strokeWidth="0.4"
                    opacity="0.9" />
            ))}

            {/* Player cards bottom (3 fan) */}
            {[[-6, 4], [0, 0], [6, 4]].map(([dx, dy], i) => (
                <rect key={i}
                    x={57 + dx} y={50 + dy}
                    width="6" height="9" rx="1"
                    fill="#9a2020"
                    stroke="rgba(210,175,70,0.4)" strokeWidth="0.4"
                    transform={`rotate(${(i - 1) * 12} ${60 + dx} ${54 + dy})`}
                    opacity="0.95" />
            ))}

            {/* Opponent cards top (2 fan) */}
            {[[-3, 0], [3, 0]].map(([dx, dy], i) => (
                <rect key={i}
                    x={58 + dx} y={15 + dy}
                    width="6" height="9" rx="1"
                    fill="#9a2020"
                    stroke="rgba(210,175,70,0.4)" strokeWidth="0.4"
                    transform={`rotate(${(i === 0 ? -14 : 14)} ${61 + dx} ${20})`}
                    opacity="0.85" />
            ))}

            {/* Accent dot (rim color) bottom right */}
            <circle cx="104" cy="62" r="3" fill={rimColor} opacity="0.45" />
        </svg>
    )
}

/* ─────────────────────────────────────────────────────────────────────────────
   SettingsDialog
───────────────────────────────────────────────────────────────────────────── */
function SettingsDialog({
    isOpen,
    onClose,
    notifPosition = "bottom-center",
    onPositionChange,
    activeTheme = "crimson",
    onThemeChange,
}) {
    const [activeSection, setActiveSection] = useState("notifications")

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) onClose()
    }, [onClose])

    const handleThemeSelect = useCallback((key) => {
        onThemeChange(key)
        applyTheme(key)
    }, [onThemeChange])

    if (!isOpen) return null

    const currentTheme = THEMES.find(t => t.key === activeTheme) || THEMES[0]

    return (
        <div className="sd-overlay" onClick={handleOverlayClick}>
            <div className="sd-dialog" role="dialog" aria-label="Settings">

                {/* ── Gold top accent line ── */}
                <div className="sd-top-accent" />

                {/* ── Header ── */}
                <div className="sd-header">
                    <div className="sd-header-icon">
                        <svg viewBox="0 0 24 24">
                            <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                        </svg>
                    </div>
                    <span className="sd-title">Settings</span>
                    <button className="sd-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* ── Body: side nav + panel ── */}
                <div className="sd-body">

                    {/* Side nav */}
                    <nav className="sd-sidemenu">
                        <button
                            className={`sd-menu-item${activeSection === "notifications" ? " sd-menu-active" : ""}`}
                            onClick={() => setActiveSection("notifications")}
                        >
                            <span className="sd-menu-icon">
                                <svg viewBox="0 0 24 24"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" /></svg>
                            </span>
                            <span>Notifications</span>
                        </button>
                        <button
                            className={`sd-menu-item${activeSection === "themes" ? " sd-menu-active" : ""}`}
                            onClick={() => setActiveSection("themes")}
                        >
                            <span className="sd-menu-icon">
                                <svg viewBox="0 0 24 24"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" /></svg>
                            </span>
                            <span>Themes</span>
                        </button>
                    </nav>

                    {/* Content panel */}
                    <div className="sd-panel">

                        {/* ── NOTIFICATIONS ── */}
                        {activeSection === "notifications" && (
                            <div className="sd-section">
                                <p className="sd-section-title">Notification Position</p>
                                <p className="sd-section-desc">Choose where game messages appear on screen</p>

                                {/* Position cards grid — each card is a mini screen preview */}
                                <div className="sd-pos-cards-grid">
                                    {POSITIONS.map(pos => {
                                        const isActive = notifPosition === pos.key

                                        return (
                                            <button
                                                key={pos.key}
                                                className={pos.key ? `sd-pos-card${isActive ? " sd-pos-card-active" : ""}` : ""}
                                                onClick={() => onPositionChange(pos.key)}
                                                title={pos.label}
                                            >
                                                {/* Mini screen mockup */}
                                                {Boolean(pos.key) && <div className="sd-pos-screen">
                                                    {/* Inner content area (simulates the game UI) */}
                                                    <div className="sd-pos-screen-inner" />
                                                    {/* Mini toast pill at the correct corner */}
                                                    <div className={`sd-pos-toast-pill sd-pos-toast-${pos.key}${isActive ? " sd-pos-toast-active" : ""}`}>
                                                        <div className="sd-pos-toast-icon" />
                                                        <div className="sd-pos-toast-lines">
                                                            <div className="sd-pos-toast-line sd-pos-toast-line-lg" />
                                                            <div className="sd-pos-toast-line sd-pos-toast-line-sm" />
                                                        </div>
                                                    </div>
                                                </div>}

                                                {/* Label + radio row */}
                                                {Boolean(pos.key) && <div className="sd-pos-card-footer">
                                                    <span className="sd-pos-card-label">{pos.label}</span>
                                                    <span className={`sd-pos-radio${isActive ? " sd-pos-radio-active" : ""}`} />
                                                </div>}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ── THEMES ── */}
                        {activeSection === "themes" && (
                            <div className="sd-section">
                                <p className="sd-section-title">Table Theme</p>
                                <p className="sd-section-desc">Choose a colour theme — applied live to your game table</p>

                                {/* Active theme big preview */}
                                <div className="sd-theme-preview-wrap">
                                    <MiniTablePreview theme={currentTheme} />
                                    <div className="sd-theme-preview-label">
                                        <span className="sd-theme-preview-name">{currentTheme.emoji} {currentTheme.label}</span>
                                        <span className="sd-theme-preview-desc">{currentTheme.desc}</span>
                                    </div>
                                </div>

                                {/* 4 theme cards */}
                                <div className="sd-themes-grid">
                                    {THEMES.map(theme => {
                                        const isActive = activeTheme === theme.key
                                        return (
                                            <button
                                                key={theme.key}
                                                className={`sd-theme-card${isActive ? " sd-theme-active" : ""}`}
                                                onClick={() => handleThemeSelect(theme.key)}
                                            >
                                                {/* Swatch strip */}
                                                <div className="sd-swatch">
                                                    {theme.swatch.map((clr, i) => (
                                                        <div key={i} className="sd-swatch-seg" style={{ background: clr }} />
                                                    ))}
                                                    {/* Rim accent line overlay */}
                                                    <div className="sd-swatch-rim" style={{ borderColor: theme.rim }} />
                                                </div>

                                                <div className="sd-theme-info">
                                                    <span className="sd-theme-emoji">{theme.emoji}</span>
                                                    <div className="sd-theme-text">
                                                        <span className="sd-theme-name">{theme.label}</span>
                                                        <span className="sd-theme-desc-sm">{theme.desc}</span>
                                                    </div>
                                                </div>

                                                {isActive && (
                                                    <span className="sd-theme-check">
                                                        <svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>
                                                    </span>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                    </div>
                </div>

            </div>
        </div>
    )
}

export default memo(SettingsDialog)